/**
 * BookingFlow: main entry point and container of all steps in the booking flow.
 *
 * Individual steps are ordered and exported from steps/index.js
 *
 * Each step is provided a parameter object:
 *
 * {
 *   state, // the current state of the flow
 *   dispatch, // function to make a change to any part of the state
 *   dispatchValueAtKeyPath, // updates a specific item in state
 *   setIsStepValid, // function which modifies the state to
 *                   //   indicate the current step is fully completed
 *                    //   and the next button can be enabled
 *   moveToNextStep, // function moves to the next step
 *   moveToPreviousStep,  // function moves to the previous step
 * }
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { keys, get, set, isEqual, map, mergeWith, isArray, filter } from 'lodash'
import {
  googleAnalyticsPageView,
  trackAddServiceToCart,
  trackRemoveServiceFromCart,
  trackBookingFlowReady,
  trackBookingFlowAbandonedDuringLoad,
  addDocumentLeaveListener,
  trackBookingFlowScrolled,
  trackBookingFlowLeft,
  usePersistedReducer,
  segmentPageView,
  useIsDesktop,
  isFreshBookingStart,
  StepNotReadyError,
} from '../../helpers'

// step definition and navigation functions
import {
  componentForStep,
  isValidStep,
  findProximateStepNumbers
} from '../../components/book/steps'

import {
  getAccessToken,
  useMe,
  loadMarkets,
  useAllMarketServices,
} from '../../services/GraphQlClientService'

import { mq, syncSavedAddressWithState } from '../../helpers'

import { useFeatureValue, useGrowthBook } from '@growthbook/growthbook-react'
import { useMarketContext } from '../../components/MarketContext'

import BookingFlowHeader from '../../components/book/steps/components/BookingFlowHeader'
import BookingFlowFooter from '../../components/book/steps/components/BookingFlowFooter'
import ValetWarningModal from '../../components/book/steps/components/ValetWarningModal'
import LoadingStep from '../../components/book/steps/components/LoadingStep'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { useRouter } from 'next/router'
import Head from 'next/head'

// we can log some additional data in development
const NEXT_PUBLIC_ENVIRONMENT = process.env.NEXT_PUBLIC_ENVIRONMENT
// const STRIPE_API_KEY = NEXT_PUBLIC_STRIPE_ELEMENTS_API_KEY = process.env
const STRIPE_API_KEY = process.env.NEXT_PUBLIC_STRIPE_ELEMENTS_API_KEY

const isDevelopment = NEXT_PUBLIC_ENVIRONMENT === 'development'

const BookingFlowMain = styled.main`
  max-width: 100vw;
  width: 100vw;
  flex: 1 0 auto;
  position: relative;

  .step-1 & {
    padding-bottom: 120px;

    ${mq('min', '768px')} {
      padding-bottom: 130px;
    }
  }

  .step-2 & {
    padding: 0 20px 120px;
    text-align: center;
  }

  .step-3 & {
    padding-bottom: 130px;
  }

  .step-4 & {
    > p + section {
      margin-top: 0;
    }

    ${mq('min', '768px')} {
      padding-bottom: 100px;
    }
  }

  .step-5 & {
    padding-bottom: 136px;
    
    ${mq('min', '768px')} {
      margin-top: 28px;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding-top: 44px;
    }
  }

  .step-6 & {
    padding-bottom: 20px;

    ${mq('min', '768px')} {
      padding-bottom: 100px;
    }
  }
`

export const getStaticProps = async () => {
  const revalidationTime = process.env.NEXT_PUBLIC_REVALIDATE_MINUTES
  const markets = await loadMarkets()

  return {
    props: {
      markets,
    },
    revalidate: revalidationTime * 60
  }
}

// The start-up-gate telemetry below must fire once per DOCUMENT load, not once
// per mount: /book can mount twice on some navigations, and the per-mount refs
// reset each time (seen in production as paired 'Booking Flow Ready' events a
// second apart). performance.timeOrigin is constant for the life of a document
// and changes on every reload, so it is the natural key.
let bookingFlowReadyReportedFor = null
// when Booking Flow Ready fired for this document (performance.now() clock),
// and whether Booking Flow Left has already gone out — both once per document
let bookingFlowReadyAt = null
let bookingFlowLeftReportedFor = null
let bookingFlowAbandonedReportedFor = null

export default function Book({ history, location, markets, code, appDispatch }) {
  // preload some stuff
  const me = useMe()
  // const search = useSearchParams()

  // console.log(search)
  // console.log(search.get('resume'))

  // load stripe
  const stripePromise = loadStripe(STRIPE_API_KEY)

  // Register Euclid with Stripe Elements so the card fields render in the brand font.
  // Stripe loads the font inside its own iframe, so the src must be an absolute URL on
  // our origin (a relative URL would resolve against js.stripe.com).
  const elementsOptions = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined
    }
    return {
      fonts: [
        {
          family: 'Euclid',
          src: `url(${window.location.origin}/fonts/EuclidCircularA-Regular.woff2)`,
          weight: '400',
          style: 'normal'
        }
      ]
    }
  }, [])

  const isDesktop = useIsDesktop()

  const growthbook = useGrowthBook()
  const straightToServiceFlag = useFeatureValue('book-straight-to-service', false)
  const dayViewFlag = useFeatureValue('day-view-web', false)
  const [flagTimedOut, setFlagTimedOut] = useState(false)
  const gbReady = Boolean(growthbook?.ready)
  useEffect(() => {
    if (gbReady) {
      return undefined
    }
    const timer = setTimeout(() => setFlagTimedOut(true), 2500)
    return () => clearTimeout(timer)
  }, [gbReady])
  const flagReady = gbReady || flagTimedOut

  // The seed below prefers the IP-detected market, which MarketContext resolves
  // asynchronously (ipdata.co round-trip on a first visit). GrowthBook usually
  // loads first, so without this gate a fresh visitor could be seeded from the
  // `markets.find(isEnabled)` fallback and never corrected once the real market
  // arrived (isFreshBookingStart is false as soon as a market is set). Wait for
  // the lookup to settle, with a timeout so a hung lookup still lets them book.
  const { selectedMarket, marketResolved } = useMarketContext()
  const [marketTimedOut, setMarketTimedOut] = useState(false)
  useEffect(() => {
    if (marketResolved) {
      return undefined
    }
    const timer = setTimeout(() => setMarketTimedOut(true), 4000)
    return () => clearTimeout(timer)
  }, [marketResolved])
  const marketReady = marketResolved || marketTimedOut

  const authedMeResolving = Boolean(getAccessToken()) && me === undefined

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatchValueAtKeyPath('isDesktop', isDesktop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop])

  // defaults for state go here
  let initialState = {
    // NOTE: do not set a default for any state synced from querystring or it will not sync
    isModalOpen: false,
    isDesktop,
    address: {
      id: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      apartment: '',
      notes: '',
      type: '',
      pets: '',
      latitude: 0,
      longitude: 0
    },
    step: 1,
    serviceRequests: [],
    referrer: (global?.document && global?.document?.referrer)
      ? new URL(global?.document?.referrer)
      : (isDevelopment
        ? 'https://www-dev.glamsquad.com/'
        : 'https://www.glamsquad.com/'
      )
  }

  // this useReducer call sets up the state of the entire booking flow
  // and the dispatch function to modify it
  const { state, reducerDispatch } = usePersistedReducer(
    (current, updated = {}) => {
      // only update the state if something has changed to avoid re-render loops
      const isChanged =
        !keys(current).length ||
        !!keys(updated).find(key => updated[key] !== current[key])

      if (!isChanged) {
        return current
      }

      const merged = mergeWith({}, current, updated, (objValue, srcValue) => {
        // if array, overwrite w/ new src value instead of merging
        if (Array.isArray(srcValue)) {
          return srcValue
        }
      })

      // merge in updated state if changed
      return merged
    },
    initialState,
    'book'
  )

  // this just wraps the reducer's dispatch, but returns null so
  // components can return from a dispatch
  const dispatch = (...args) => {
    reducerDispatch(...args)
    return null
  }

  // Enrollment belongs to the flow, rather than the current rollout. Existing
  // day-view selections can be recognized before GrowthBook finishes loading.
  const hasSelectedAppointment = Boolean(state.selectedAppointment)
  const hasDayViewSelection = Boolean(
    state.selectedAppointment?.bookingTokens?.length
  )
  const hasPersistedBookingArm = typeof state.dayViewBooking === 'boolean'
  useEffect(() => {
    if (
      state.step >= 2 &&
      state.step <= 5 &&
      !hasPersistedBookingArm &&
      (hasSelectedAppointment || flagReady)
    ) {
      reducerDispatch({
        dayViewBooking: hasSelectedAppointment ? hasDayViewSelection : dayViewFlag
      })
    }
  }, [
    state.step,
    hasPersistedBookingArm,
    hasSelectedAppointment,
    hasDayViewSelection,
    flagReady,
    dayViewFlag,
    reducerDispatch
  ])

  // dispatches a change to state if value at keyPath has changed
  const dispatchValueAtKeyPath = (keyPath, value) => {
    // do not dispatch if unchanged
    if (isEqual(get(state, keyPath), value)) {
      return null
    }

    return dispatch(set({ ...state }, keyPath, value))
  }

  const { query } = useRouter()

  const marketShortName = query?.mkt || undefined
  const serviceName = query?.svc || undefined
  const serviceType = query?.stp || undefined

  // A ?mkt= deep link names a market before one is in booking state. The catalog has to
  // be loaded for that market or the effect below can never resolve its service, so it
  // is the fallback when state.market is not set yet.
  const deepLinkMarket = useMemo(
    () =>
      markets?.find(
        ({ shortName }) =>
          shortName &&
          marketShortName &&
          shortName.toLowerCase() === marketShortName.toLowerCase()
      ),
    [markets, marketShortName]
  )

  // selectedMarket is the prefetch, and it matters most for the straight-to-service
  // arm. state.market is not set until the seeding effect runs, which happens at the
  // same moment showLoader clears — so keying the catalog on it alone would start this
  // fetch exactly where it used to finish, and step two would render its own loader for
  // a round trip that used to overlap the flag/me/market wait. MarketContext resolves
  // independently of that gate and is what the seed picks in the common case, so the
  // catalog is usually warm by the time a market becomes official. A seed that lands on
  // a different market (a saved address outranks the detected one) just refetches; the
  // response is one market's ~33 rows and CloudFront-cached.
  const marketServices = useAllMarketServices(
    state.market?.id || deepLinkMarket?.id || selectedMarket?.id
  )

  // check if a market has been provided and select if found
  useEffect(() => {
    if (!(markets && marketShortName)) {
      return
    }

    const market = deepLinkMarket
    if (!market) {
      return
    }

    if (!marketServices) {
      return
    }

    const marketService = marketServices
      .filter(({ marketId }) => marketId === market.id)
      .find(
        ({ service: { name } }) =>
          serviceName && serviceName.toLowerCase() === name.toLowerCase()
      )

    dispatch({
      step: 2,
      allowAddressSelect: false,
      market: market,
      displayedMarketService: marketService,
      isMarketServiceModalOpen: !!marketServices,
      scrollToServiceType: serviceType,
      address: {
        id: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        apartment: '',
        notes: '',
        type: '',
        pets: '',
        latitude: 0,
        longitude: 0
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketShortName, JSON.stringify(markets), JSON.stringify(marketServices), serviceName, serviceType])

  useEffect(() => {
    if (!flagReady || !straightToServiceFlag) {
      return
    }
    if (!isFreshBookingStart(state, query)) {
      return
    }
    if (authedMeResolving) {
      return
    }
    // Not yet: the IP-detected market is still in flight (see marketReady above).
    if (!marketReady) {
      return
    }
    const savedMarketId = me?.addresses?.find(a => !!a.marketId)?.marketId
    const knownMarket =
      (savedMarketId && markets?.find(m => m.id === savedMarketId && m.isEnabled)) ||
      (selectedMarket && markets?.find(m => m.id === selectedMarket.id && m.isEnabled)) ||
      null
    const seedMarket = knownMarket || markets?.find(m => m.isEnabled) || null
    if (!seedMarket) {
      return
    }
    // A seed taken from the first-enabled fallback (the lookup timed out, or
    // came back empty) is PROVISIONAL: remember which market we guessed so the
    // effect below can swap in the detected one if it arrives late. A seed from
    // the saved address or a settled lookup is final.
    dispatch({ step: 2, market: seedMarket, provisionalMarketId: knownMarket ? null : seedMarket.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagReady, straightToServiceFlag, JSON.stringify(markets), selectedMarket, marketReady, me])

  // Late correction for a provisional seed: the IP lookup settled after the
  // timeout above. Only while the guess is still untouched — same market we
  // seeded, still on service selection, nothing in the cart, no address — so a
  // client who has already acted on the guessed market is never yanked away.
  useEffect(() => {
    const { provisionalMarketId, market, step: currentStep, serviceRequests: cart, address: addr } = state
    if (!provisionalMarketId || !selectedMarket || selectedMarket.id === provisionalMarketId) {
      return
    }
    if (market?.id !== provisionalMarketId || currentStep !== 2 || cart?.length || addr?.street) {
      return
    }
    const detected = markets?.find(m => m.id === selectedMarket.id && m.isEnabled)
    if (!detected) {
      return
    }
    dispatch({ market: detected, provisionalMarketId: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarket, state.provisionalMarketId, state.market?.id, state.step])

  // Start-up gate for fresh starts and resumed experiment steps. Computed
  // here — after state/query exist and before any early return — so the
  // telemetry effects can observe it without violating the rules of hooks.
  const showLoader =
    ((!flagReady || (straightToServiceFlag && (authedMeResolving || !marketReady))) &&
      isFreshBookingStart(state, query)) ||
    (state.step >= 2 && state.step <= 3 && !hasPersistedBookingArm &&
      (!flagReady || hasSelectedAppointment))

  // Telemetry for that gate: how long fresh starts wait, on what, and per arm —
  // and who leaves before it clears (trackBookingFlowAbandonedDuringLoad goes
  // out as a beacon because the page is unloading). Fires once per mount.
  // The gate values are mirrored into a ref on every render so the pagehide
  // handler reports what is STILL open at the moment of leaving, not what was
  // open when the loader first appeared.
  const gateRef = useRef(null)
  gateRef.current = {
    waitedOn: [
      !flagReady && 'flag',
      straightToServiceFlag && authedMeResolving && 'me',
      straightToServiceFlag && !marketReady && 'market'
    ].filter(Boolean),
    timedOut: flagTimedOut || marketTimedOut,
    straightToService: straightToServiceFlag
  }
  const loaderStartRef = useRef(null)
  const loaderWaitedOnAtStartRef = useRef([])
  const loaderReportedRef = useRef(false)
  const freshStartAtMountRef = useRef(null)
  if (freshStartAtMountRef.current === null) {
    freshStartAtMountRef.current = isFreshBookingStart(state, query)
  }
  useEffect(() => {
    if (!freshStartAtMountRef.current || loaderReportedRef.current) {
      return undefined
    }
    if (bookingFlowReadyReportedFor === performance.timeOrigin) {
      // a previous mount of this document already reported the gate
      loaderReportedRef.current = true
      return undefined
    }
    if (showLoader) {
      if (loaderStartRef.current === null) {
        loaderStartRef.current = performance.now()
        loaderWaitedOnAtStartRef.current = gateRef.current.waitedOn
      }
      const onLeave = () => {
        // addDocumentLeaveListener can fire on both hide and unload
        if (bookingFlowAbandonedReportedFor === performance.timeOrigin) {
          return
        }
        bookingFlowAbandonedReportedFor = performance.timeOrigin
        trackBookingFlowAbandonedDuringLoad({
          elapsedMs: performance.now() - loaderStartRef.current,
          waitedOn: gateRef.current.waitedOn,
          straightToService: gateRef.current.straightToService
        })
      }
      return addDocumentLeaveListener(onLeave)
    }
    loaderReportedRef.current = true
    bookingFlowReadyReportedFor = performance.timeOrigin
    bookingFlowReadyAt = performance.now()
    trackBookingFlowReady({
      waitMs: loaderStartRef.current === null ? 0 : performance.now() - loaderStartRef.current,
      waitedOn: loaderWaitedOnAtStartRef.current,
      timedOut: gateRef.current.timedOut,
      straightToService: gateRef.current.straightToService
    })
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLoader])

  // Engagement telemetry once the gate has cleared: scroll-depth thresholds
  // (once each per step) and a beaconed `Booking Flow Left` the first time the
  // document is hidden or unloaded (see addDocumentLeaveListener) with
  // time-on-flow, whether the client ever interacted, and the cart size — the
  // pieces a bounce metric needs that no click event can provide.
  const engagementRef = useRef({ interacted: false, maxDepth: 0, depthsForStep: null, stepKey: null })
  engagementRef.current.step = state.step
  engagementRef.current.cartSize = state.serviceRequests?.length || 0
  engagementRef.current.straightToService = straightToServiceFlag
  useEffect(() => {
    if (showLoader || typeof window === 'undefined') {
      return undefined
    }
    const eng = engagementRef.current
    const markInteracted = () => {
      eng.interacted = true
    }
    // rAF-guarded like the nav's scroll listener: layout reads happen at most
    // once per frame however fast scroll events arrive
    let scrollFrame = null
    const onScroll = () => {
      if (scrollFrame !== null) {
        return
      }
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = null
        measureScroll()
      })
    }
    const measureScroll = () => {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      if (scrollable <= 0) {
        return
      }
      const pct = ((window.scrollY || doc.scrollTop) / scrollable) * 100
      if (eng.stepKey !== eng.step) {
        eng.stepKey = eng.step
        eng.depthsForStep = new Set()
      }
      for (const depth of [25, 50, 75, 100]) {
        if (pct >= depth && !eng.depthsForStep.has(depth)) {
          eng.depthsForStep.add(depth)
          eng.maxDepth = Math.max(eng.maxDepth, depth)
          trackBookingFlowScrolled({ depth, step: eng.step, straightToService: eng.straightToService })
        }
      }
    }
    const onLeave = () => {
      if (bookingFlowReadyReportedFor !== performance.timeOrigin || bookingFlowLeftReportedFor === performance.timeOrigin) {
        return
      }
      bookingFlowLeftReportedFor = performance.timeOrigin
      trackBookingFlowLeft({
        timeOnPageMs: performance.now() - (bookingFlowReadyAt ?? performance.now()),
        step: eng.step,
        interacted: eng.interacted,
        scrolledDepth: eng.maxDepth,
        cartSize: eng.cartSize,
        straightToService: eng.straightToService
      })
    }
    const opts = { passive: true }
    document.addEventListener('pointerdown', markInteracted, opts)
    document.addEventListener('keydown', markInteracted, opts)
    window.addEventListener('scroll', onScroll, opts)
    const removeLeaveListener = addDocumentLeaveListener(onLeave)
    return () => {
      if (scrollFrame !== null) {
        window.cancelAnimationFrame(scrollFrame)
      }
      document.removeEventListener('pointerdown', markInteracted)
      document.removeEventListener('keydown', markInteracted)
      window.removeEventListener('scroll', onScroll)
      removeLeaveListener()
    }
  }, [showLoader])

  //Once user logs in, if the address saved matches current, update to saved.
  syncSavedAddressWithState(me, state, dispatch)

  // valet modal
  const [valetModalOpen, setValetModalOpen] = useState(false)
  const [valetModalTitle, setValetModalTitle] = useState(undefined)
  const [valetModalExplanation, setValetModalExplanation] = useState(undefined)
  const closeValetModal = () => {
    setValetModalOpen(false)
    setValetModalTitle(undefined)
    setValetModalExplanation(undefined)
  }

  // extract some step info from state
  let { step, isStepValid, nextButtonLabel = 'Select Your Service', isModalOpen, selectedAppointment } = state

  useEffect(() => {
    // log each change in step as a page view to google analytics
    // two pageviews for each step in order to distinguish in GA
    const pageviewForStep = [
      '',
      ['/book/v3/'],
      ['/book/v3/services'],
      ['/book/v3/availability-search'],
      ['/book/v3/sign-in'],
      ['/book/v3/payment'],
      ['/book/v3/confirmation', '/book/confirmation']
    ]

    const pageview = pageviewForStep[step]
    // convert to array of pageviews unless it is already
    const pageviews = isArray(pageview) ? pageview : [pageview]

    window.history.replaceState(undefined, undefined, `/book/${step}`)

    pageviews.forEach(pageview => {
      googleAnalyticsPageView(pageview)
      segmentPageView(pageview)
    })
  }, [step])

  /**
   * Keep the Intercom launcher clear of the fixed booking footer (QA: it overlapped
   * the "Book Now" CTA on mobile and sat on top of the footer bar on desktop).
   * Intercom's default vertical_padding is 20px from the viewport bottom, which puts
   * the launcher inside the footer, so lift it to 20px above the footer's top edge.
   *
   * Polled + diffed rather than applied once because none of the moving parts are
   * synchronous: Intercom boots asynchronously after sign-in (which happens mid-flow
   * at step 4, and would reset vertical_padding to the default), and the footer's
   * height varies by step, viewport, and whether it's mounted at all (steps 1/4/6).
   * update() is only called when the computed padding actually changes — Intercom
   * throttles frequent settings updates, so identical values must not be re-sent.
   */
  useEffect(() => {
    const DEFAULT_PADDING = 20
    let applied = null

    const sync = () => {
      if (!window.Intercom) {
        return
      }
      const footer = document.getElementById('booking-flow-footer')
      const padding = footer
        ? Math.ceil(footer.getBoundingClientRect().height) + DEFAULT_PADDING
        : DEFAULT_PADDING
      if (padding !== applied) {
        window.Intercom('update', { vertical_padding: padding })
        applied = padding
      }
    }

    sync()
    const intervalId = window.setInterval(sync, 1000)

    return () => {
      window.clearInterval(intervalId)
      // restore the site-wide default when leaving the booking flow
      if (window.Intercom && applied !== null && applied !== DEFAULT_PADDING) {
        window.Intercom('update', { vertical_padding: DEFAULT_PADDING })
      }
    }
  }, [])

  if (!step) {
    return null
  }

  // Hold fresh starts on the loader until everything the straight-to-service
  // seed depends on has settled: the flag itself, the signed-in profile (saved
  // address wins), and the IP-detected market. Otherwise step 1 would flash
  // before enrollment, or the variant would be seeded from the wrong market.
  if (showLoader) {
    return <LoadingStep />
  }

  // include authentication status in w/ the state just to make it available
  state.isAuthenticated = !!getAccessToken()

  state.straightToService = straightToServiceFlag

  const [nextStep, previousStep] = findProximateStepNumbers(step, state)

  // enabled if step w/in range and current state passes current step's validation
  const nextButtonEnabled = isValidStep(nextStep) && isStepValid

  // enabled if step w/in range
  const previousButtonEnabled = isValidStep(previousStep)

  // fns to change current step
  const moveToNextStep = () => {
    const travelModeType = state?.market?.travelTimeCalculationOptions?.type
    const shouldShowValetModal = state?.address?.type === 'Hotel' && travelModeType?.toLowerCase() === 'driving'
    
    if (step === 3 && (shouldShowValetModal || selectedAppointment?.additionalFees?.length)) {
      setValetModalOpen(true)
      setValetModalTitle(selectedAppointment?.additionalFees?.[0]?.description)
      setValetModalExplanation(selectedAppointment?.additionalFees?.[0]?.explanation)
    } else {
      dispatchValueAtKeyPath('step', nextStep)
      window.scrollTo(0, 0)
    }
  }

  const moveToPreviousStep = () => {
    dispatchValueAtKeyPath('step', previousStep)
    window.scrollTo(0, 0)
  }

  isDevelopment &&
    console.log(
      JSON.stringify(
        {
          previousStep: previousStep,
          currentStep: step,
          nextStep: nextStep,
          isAuthenticated: state.isAuthenticated,
          market: get(state, 'market.shortName', 'NOT SET'),
          address: get(state, 'address.street', 'NOT SET')
        },
        null,
        2
      )
    )

  let next
  let platformPayConfirm // set by Step Five via setIsStepValid's 4th arg; read lazily at confirm time

  // function passed down to steps
  const setIsStepValid = (
    isStepValid,
    nextButtonLabel = 'Next Step',
    stepNext,
    platformConfirm // optional; only Step Five supplies it
  ) => {
    next = stepNext || moveToNextStep
    // Only replace the wallet callback when the step supplies one. Step Five
    // re-registers without it while its profile reloads (not-ready render); an
    // Apple / Google Pay confirm already in flight holds this render's binding
    // and must still find the callback once the wallet authorizes, or the hold
    // would be left without an appointment. Non-payment steps never set it and
    // every page render starts with it undefined, so nothing stale survives.
    if (platformConfirm) {
      platformPayConfirm = platformConfirm
    }

    if (isStepValid === state.isStepValid
      && nextButtonLabel === state.nextButtonLabel) {
      return
    }
    dispatch({ isStepValid, nextButtonLabel })
  }

  // open and close modal
  const openModal = () => {
    dispatchValueAtKeyPath('isModalOpen', true)
    return false
  }

  const closeModal = () => {
    dispatchValueAtKeyPath('isModalOpen', false)
    return false
  }

  const { serviceRequests } = state

  // dispatches state update to select a market service
  const selectMarketService = ({
    marketService,
    addOnMarketServices = [],
    instances = 1,
    backToBack = false
  }) => {
    const {
      service: { id: serviceId }
    } = marketService

    const serviceRequest = {
      serviceId,
      instances,
      backToBack,
      addOnServiceIds: map(addOnMarketServices, 'service.id')
    }

    dispatch({
      // filter out same service to replace if present and append new request
      serviceRequests: serviceRequests
        .filter(sr => sr.serviceId !== serviceId)
        .concat([serviceRequest]),
      isModalOpen: false
    })

    trackAddServiceToCart(state.market, marketService, instances, addOnMarketServices)
  }

  // dispatches state update to deselect a market service
  const deselectMarketService = serviceId => {
    const removedService = filter(marketServices, ms => ms.marketId === state.market.id)
      .find(ms => ms.service.id === serviceId)

    trackRemoveServiceFromCart(state.market, removedService, serviceRequests.find(sr => sr.serviceId === serviceId))

    const remainingServiceRequests = serviceRequests.filter(sr => sr.serviceId !== serviceId)

    // Changing the cart on the availability search step invalidates the results
    // on screen — they were searched, staffed and priced for the old set of
    // services — but nothing extra is needed to refresh them: serviceRequests is
    // one of useAppointmentAvailabilitySearch's stringified deps, so the search
    // re-runs, and appointmentAvailabilitySearch clears selectedAppointment as
    // its first act.
    //
    // Emptying the cart is the exception. There is nothing left to search for,
    // so the user goes back to service selection — and because that leaves the
    // search step, no re-run follows to clear the now-meaningless selection.
    // Both of those ship in one dispatch: dispatchValueAtKeyPath builds its
    // payload from this render's captured state, so a second call would carry
    // the stale serviceRequests and undo the removal.
    if (!remainingServiceRequests.length && step === 3) {
      return dispatch({
        ...state,
        serviceRequests: remainingServiceRequests,
        selectedAppointment: null,
        step: 2,
        // isStepValid still holds step 3's verdict. Step Two recomputes it from
        // serviceRequests as it renders, but that is one render later — and for
        // that render the footer would read a stale `true` and enable Next on an
        // empty cart. Carry the correct value in with the step change rather
        // than leaning on React to re-render before the browser paints.
        isStepValid: false
      })
    }

    dispatchValueAtKeyPath('serviceRequests', remainingServiceRequests)
  }

  // get the current step component
  const CurrentStep = componentForStep(step)

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <Head>
        <title>Book | Glamsquad</title>
      </Head>
      <div className={`booking-flow step-${step}`}>
        <BookingFlowHeader
          {...{
            step,
            prev: previousButtonEnabled && moveToPreviousStep,
            referrer: state.referrer,
            history,
            straightToService: state.straightToService
          }}
        />
        <BookingFlowMain>
          <CurrentStep
            {...{
              state,
              dispatch,
              dispatchValueAtKeyPath,
              setIsStepValid,
              moveToNextStep,
              moveToPreviousStep,
              nextStep,
              previousStep,
              isModalOpen,
              openModal,
              closeModal,
              selectMarketService,
              deselectMarketService,
              me,
              next: () => next(),
              nextButtonEnabled,
              nextButtonLabel,
              step,
              history,
              location,
              code,
              appDispatch
            }}
          />
        </BookingFlowMain>
        <BookingFlowFooter
          {...{
            state,
            // Call whatever next function the current step registered. A step that
            // bails out of its render before calling setIsStepValid (Step Five while
            // the profile reloads) leaves `next` unset for that render, so surface
            // that as a StepNotReadyError the footer reports and explains instead of
            // the bare "t is not a function" TypeError it used to throw.
            next: (...args) => {
              if (typeof next !== 'function') {
                throw new StepNotReadyError()
              }
              return next(...args)
            },
            // Money path: fail loud, never silent. The closure var above (declared line ~370)
            // is assigned by Step Five via setIsStepValid's 4th arg during render. If it is
            // somehow absent when the wallet fires confirm, the PaymentIntent has ALREADY been
            // authorized — so throwing here surfaces the failure through PlatformPayButton's
            // catch -> onError (which, per Task 1, also tracks it) instead of dropping the
            // booking with no trace. `platformPayConfirm` inside this arrow resolves to the
            // outer `let` (the object key does not create a binding; there is no shadowing).
            platformPayConfirm: (...args) => {
              if (typeof platformPayConfirm !== 'function') {
                throw new Error("We couldn't complete your booking. Please try again.")
              }
              return platformPayConfirm(...args)
            },
            nextButtonEnabled,
            nextButtonLabel,
            deselectMarketService,
            dispatchValueAtKeyPath
          }}
        />
        <ValetWarningModal
          isOpen={valetModalOpen}
          close={closeValetModal}
          isDesktop={isDesktop}
          nextStep={nextStep}
          dispatch={dispatchValueAtKeyPath}
          title={valetModalTitle}
          explanation={valetModalExplanation}
        />
      </div>
    </Elements>
  )
}
